import { openNotificationModal } from 'store/use-notification-modal-store';
import { useUserProfileValidation } from './query';
import { AuthRole, NotificationType } from 'common/enums/enums';
import { useHasRoles } from './use-has-roles.hook';
import { NotificationModalState } from 'common/types/types';

type Arguments = {
  modalProps: {
    actionText: string;
    onClose?: NotificationModalState['onClose'];
  };
};

const useUnverifiedProfileActionGuard = (args: Arguments) => {
  const { modalProps } = args;
  const { data } = useUserProfileValidation();
  const isLandlord = useHasRoles([AuthRole.Landlord]);

  const getUserHasVerification = () => {
    if (isLandlord && !Boolean(data?.isIdentityEnabled)) {
      return Boolean(data?.isProfileValid);
    }

    return Boolean(data?.isProfileValid && data.isIdentityValid);
  };

  const getUserHasNoPenalties = () => {
    return Boolean(data?.isNoPenalty);
  };

  const openModal = (reason: 'profile' | 'penalty') => {
    const modalConfig = reason === 'profile'
      ? {
        title: 'Additional information required',
        description: `You are unable ${modalProps.actionText} because your profile is incomplete. To ensure a smooth and secure experience, please fill out your profile with all the required information`,
      }
      : {
        title: 'Service Fees Action Required',
        description: `This action is currently unavailable. To proceed, a service fee of [$150] is required. This fee was applied because the lease agreement was not countersigned within the required 48-hour window after renter acceptance, as outlined in Axxelist's Terms and Conditions. We kindly ask that payment be made at your earliest convenience. If you have any questions or need assistance, our support team will be happy to help.`,
      };

    openNotificationModal({
      type: NotificationType.WARNING,
      title: modalConfig.title,
      description: modalConfig.description,
      onClose: modalProps.onClose,
    });
  };

  const withActionGuard = (action: () => void) => {
    const isUserVerified = getUserHasVerification();
    const userHasNoPenalties = getUserHasNoPenalties();

    if (!isUserVerified) {
      openModal('profile');
    } else if (isLandlord && !userHasNoPenalties) {
      openModal('penalty');
    } else {
      action();
    }
  };

  return {
    withActionGuard,
  };
};

export { useUnverifiedProfileActionGuard };
